import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChooseListInput } from '@/shared/components/inputs/ChooseList.input';
import { Card, CardContent } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { WizardStepProps } from '../types/wizard.types';
import { ComponentReturnTypeMap } from '@momentum/types';

interface ChoiceOption {
  id: string;
  title: string;
  description?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  exclusive?: boolean; // If true, selecting this option disables all others
}

interface WizardChoiceListStepProps
  extends WizardStepProps<ComponentReturnTypeMap['ChoiceList']> {
  options: ChoiceOption[];
  columns?: 1 | 2 | 3;
  required?: boolean;
  multiSelect?: boolean;
  maxItems?: number;
}

export const WizardChoiceListStep: React.FC<WizardChoiceListStepProps> = ({
  value,
  onChange,
  onNext,
  onPrevious: _onPrevious, // Required by WizardStepProps but not used
  error,
  disabled,
  options,
  columns = 2,
  required = false,
  multiSelect = false,
  maxItems,
  setSubmitHandler,
}) => {
  const { t } = useTranslation('wizard');
  // Internal state for multi-select to prevent auto-advance
  const [internalSelection, setInternalSelection] = useState<string[]>([]);

  // Initialize internal selection from value
  useEffect(() => {
    if (multiSelect && value) {
      if (Array.isArray(value)) {
        setInternalSelection(value);
      } else {
        setInternalSelection([value]);
      }
    }
  }, [multiSelect, value]);

  const handleChange = (selectedId: string | string[]) => {
    if (disabled) return;

    if (multiSelect) {
      // For multi-select, only update internal state - don't call onChange yet
      const newSelection = Array.isArray(selectedId)
        ? selectedId
        : [selectedId];

      // Check if any of the newly selected options is exclusive
      const exclusiveOption = newSelection.find((id) =>
        options.find((opt) => opt.id === id && opt.exclusive)
      );

      let finalSelection: string[] = [];

      if (exclusiveOption) {
        // If an exclusive option is selected, only keep that one
        finalSelection = [exclusiveOption];
      } else {
        // If no exclusive option in new selection, remove any previously selected exclusive options
        finalSelection = newSelection.filter(
          (id) => !options.find((opt) => opt.id === id && opt.exclusive)
        );

        // Check maxItems limit
        if (maxItems && finalSelection.length > maxItems) {
          // Don't update if it would exceed the limit
          return;
        }
      }

      setInternalSelection(finalSelection);
    } else {
      // Single selection - pass the value and auto-advance
      const singleValue = Array.isArray(selectedId)
        ? selectedId[0]
        : selectedId;
      if (singleValue) {
        onChange([singleValue]);
        // Auto-advance for single select
        if (onNext) {
          setTimeout(() => {
            onNext([singleValue]);
          }, 100); // Small delay for better UX
        }
      }
    }
  };

  // Handle manual next for multi-select
  const handleManualNext = () => {
    if (multiSelect && internalSelection.length > 0) {
      // Map selected IDs to full objects
      const selectedObjects = internalSelection
        .map((id) => options.find((opt) => opt.id === id))
        .filter(Boolean); // Remove any undefined values

      onNext(selectedObjects.map((obj) => obj?.id || '')); // This will trigger wizard advancement
    }
  };

  // Register submit handler for the card footer's Next button
  const typedSetSubmitHandler = setSubmitHandler as
    | ((handler: (() => void) | null) => void)
    | undefined;
  if (multiSelect) {
    typedSetSubmitHandler?.(handleManualNext);
  }

  // Compute disabled options based on exclusive selection
  const isExclusiveSelected =
    multiSelect &&
    internalSelection.some((id) =>
      options.find((opt) => opt.id === id && opt.exclusive)
    );

  const computedOptions = multiSelect
    ? options.map((opt) => ({
        ...opt,
        // Disable non-exclusive options when an exclusive option is selected
        // Don't disable the exclusive option itself
        disabled:
          opt.disabled || (isExclusiveSelected && !opt.exclusive) || false,
      }))
    : options;

  return (
    <div className="w-full mx-auto space-y-6">
      <Card className="border-none shadow-none bg-transparent">
        <CardContent className="p-0">
          {multiSelect ? (
            <ChooseListInput
              options={computedOptions}
              value={internalSelection}
              onChange={handleChange}
              variant="card"
              columns={columns}
              required={required}
              disabled={disabled}
              className="w-full"
              multiSelect={true}
            />
          ) : (
            <ChooseListInput
              options={computedOptions}
              value={value?.[0] || ''}
              onChange={handleChange}
              variant="card"
              columns={columns}
              required={required}
              disabled={disabled}
              className="w-full"
              multiSelect={false}
            />
          )}
        </CardContent>
      </Card>

      {/* Multi-select specific UI */}
      {multiSelect && (
        <div className="space-y-3">
          {/* Selection count and max items info */}
          <div className="text-sm text-muted-foreground text-center">
            {internalSelection.length > 0 ? (
              <span>
                {internalSelection.length === 1
                  ? t('common.messages.itemsSelectedSingular')
                  : t('common.messages.itemsSelectedPlural', {
                      count: internalSelection.length,
                    })}
                {maxItems && <span className="ml-1">({maxItems})</span>}
              </span>
            ) : (
              maxItems && (
                <span>
                  {maxItems > 1
                    ? t('common.messages.selectUpToPlural', {
                        count: maxItems,
                      })
                    : t('common.messages.selectUpToSingular')}
                </span>
              )
            )}
          </div>

          {/* Max items warning */}
          {maxItems && internalSelection.length >= maxItems && (
            <div className="text-sm text-amber-600 bg-amber-50 p-2 rounded-md text-center">
              {t('common.messages.maxItemsSelected', { count: maxItems })}
            </div>
          )}

          {/* Continue button — hidden when card footer handles it */}
          {!typedSetSubmitHandler && (
            <div className="flex justify-center">
              <Button
                onClick={handleManualNext}
                disabled={internalSelection.length < 1 || disabled}
                className="px-8"
              >
                {t('common.buttons.continue')}
              </Button>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
          {error}
        </div>
      )}
    </div>
  );
};
